import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Mail, MessageCircle, HelpCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HelpCenter = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="help-center-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-4">Contact Us & Help Center</h1>
          <p className="text-muted-foreground text-lg mb-12">
            If you need assistance regarding your order, replacement requests, or general inquiries, please contact our support team through the official channels below.
          </p>
          
          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <a 
              href="mailto:Support@xtreamstore.in"
              className="p-8 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all hover:scale-105 group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-2">Support Email</h3>
              <p className="text-primary font-bold mb-2">Support@xtreamstore.in</p>
              <p className="text-sm text-muted-foreground">We typically respond within 24 hours</p>
            </a>
            
            <a 
              href="https://t.me/Xtreamstoresupport"
              target="_blank"
              rel="noopener noreferrer"
              className="p-8 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all hover:scale-105 group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#0088cc]/10 flex items-center justify-center mb-4 group-hover:bg-[#0088cc]/20 transition-colors">
                <MessageCircle className="w-6 h-6 text-[#0088cc]" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-2">Telegram Support</h3>
              <p className="text-[#0088cc] font-bold mb-2">@Xtreamstoresupport</p>
              <p className="text-sm text-muted-foreground">Real-time assistance on Telegram</p>
            </a>
          </div>

          {/* Important Notes */}
          <div className="bg-primary/10 p-6 rounded-xl border border-primary/20 mb-12">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-heading font-bold mb-3">Important Notes:</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Always contact us within the warranty / validity period for replacement-related issues</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Please share order details and proof (screenshots or screen recording) for faster resolution</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Support response time may vary based on request volume</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>Xtream Store reserves the right to verify all claims before providing support or replacement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="text-3xl font-heading font-black mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h3 className="text-lg font-heading font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  How do I request a replacement?
                </h3>
                <p className="text-muted-foreground">
                  Contact us via email or Telegram within the warranty period with your order ID and proof of the issue (screenshots/recording). Our team will verify and process your replacement request.
                </p>
              </div>
              
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h3 className="text-lg font-heading font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  What information should I include in my support request?
                </h3>
                <p className="text-muted-foreground">
                  Include your order ID, registered email, description of the issue, and relevant screenshots or recordings showing the problem.
                </p>
              </div>
              
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h3 className="text-lg font-heading font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  Can I get a refund?
                </h3>
                <p className="text-muted-foreground">
                  No, we have a strict no-refund policy. However, replacements are provided for issues within the warranty period as per our terms and conditions.
                </p>
              </div>
              
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h3 className="text-lg font-heading font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  How long does it take to get a replacement?
                </h3>
                <p className="text-muted-foreground">
                  Once your claim is verified, replacements are typically processed within 24-48 hours. Response times may vary based on the volume of requests.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default HelpCenter;